/**
 * Boundary and constraint utilities for canvas elements
 * Extracted from Canvas.jsx to reduce file size
 */
import { isShapeType } from "../../../util/shapeTypeMapper";
import {
  FIXED_MAIN_CANVAS_STAGE_WIDTH_PX,
  FIXED_OFF_CANVAS_SIDE_WIDTH_PX,
} from "./mainCanvasLayout";

/**
 * Helper function to get the current pointer position relative to a given stage.
 * @param {Konva.Stage} stage The Konva Stage instance.
 * @returns {object} {x, y} coordinates.
 */
export const getRelativePointerPosition = (stage) => {
  if (!stage) return { x: 0, y: 0 };
  const transform = stage.getPointerPosition();
  return transform
    ? {
        x: (transform.x - stage.x()) / stage.scaleX(),
        y: (transform.y - stage.y()) / stage.y(),
      }
    : { x: 0, y: 0 };
};

/**
 * Boundary constraint function to keep elements within canvas bounds
 */
export const constrainToBoundaries = (
  x,
  y,
  width,
  height,
  stageRef,
  elementType = null,
  points = null
) => {
  if (!stageRef?.current) return { x, y };

  const stage = stageRef.current;
  const stageWidth = stage.width();
  const stageHeight = stage.height();

  // Special handling for arrows and lines that use points arrays
  if (
    (isShapeType(elementType, "Arrow") ||
      isShapeType(elementType, "Line") ||
      isShapeType(elementType, "DoubleSidedArrow")) &&
    points &&
    points.length > 0
  ) {
    // Calculate the bounds of all points
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    for (let i = 0; i < points.length; i += 2) {
      const pointX = points[i];
      const pointY = points[i + 1];
      minX = Math.min(minX, pointX);
      maxX = Math.max(maxX, pointX);
      minY = Math.min(minY, pointY);
      maxY = Math.max(maxY, pointY);
    }

    const arrowWidth = maxX - minX;
    const arrowHeight = maxY - minY;

    // Calculate boundaries with padding
    const padding = 20;
    const minBoundX = padding;
    const maxBoundX = stageWidth - arrowWidth - padding;
    const minBoundY = padding;
    const maxBoundY = stageHeight - arrowHeight - padding;

    // Check if arrow is outside bounds
    const isOutside =
      minX < minBoundX ||
      maxX > maxBoundX + arrowWidth ||
      minY < minBoundY ||
      maxY > maxBoundY + arrowHeight;

    if (isOutside) {
      // Calculate the offset needed to bring arrow back inside bounds
      let offsetX = 0;
      let offsetY = 0;

      if (minX < minBoundX) {
        offsetX = minBoundX - minX;
      } else if (maxX > maxBoundX + arrowWidth) {
        offsetX = maxBoundX + arrowWidth - maxX;
      }

      if (minY < minBoundY) {
        offsetY = minBoundY - minY;
      } else if (maxY > maxBoundY + arrowHeight) {
        offsetY = maxBoundY + arrowHeight - maxY;
      }

      return {
        x: x + offsetX,
        y: y + offsetY,
        points: points.map((coord, i) =>
          i % 2 === 0 ? coord + offsetX : coord + offsetY
        ),
      };
    }

    return { x, y, points };
  }

  // Standard handling for other elements
  const elementWidth = width || 100;
  const elementHeight = height || 100;

  // Calculate boundaries with padding
  const padding = 20;
  const minX = padding;
  const maxX = stageWidth - elementWidth - padding;
  const minY = padding;
  const maxY = stageHeight - elementHeight - padding;

  // Constrain coordinates
  const constrainedX = Math.max(minX, Math.min(maxX, x));
  const constrainedY = Math.max(minY, Math.min(maxY, y));

  return { x: constrainedX, y: constrainedY };
};

/**
 * Function to check if element is outside boundaries and move it back
 */
export const ensureElementInBounds = (element, stageRef) => {
  if (!stageRef?.current) return element;

  const stage = stageRef.current;
  const stageWidth = stage.width();
  const stageHeight = stage.height();

  // Special handling for arrows and lines that use points arrays
  if (
    (isShapeType(element.type, "Arrow") ||
      isShapeType(element.type, "Line") ||
      isShapeType(element.type, "DoubleSidedArrow")) &&
    element.points &&
    element.points.length > 0
  ) {
    // Calculate the bounds of all points
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    for (let i = 0; i < element.points.length; i += 2) {
      const pointX = element.points[i];
      const pointY = element.points[i + 1];
      minX = Math.min(minX, pointX);
      maxX = Math.max(maxX, pointX);
      minY = Math.min(minY, pointY);
      maxY = Math.max(maxY, pointY);
    }

    const arrowWidth = maxX - minX;
    const arrowHeight = maxY - minY;

    // Calculate boundaries with padding
    const padding = 20;
    const minBoundX = padding;
    const maxBoundX = stageWidth - arrowWidth - padding;
    const minBoundY = padding;
    const maxBoundY = stageHeight - arrowHeight - padding;

    // Check if arrow is outside bounds
    const isOutside =
      minX < minBoundX ||
      maxX > maxBoundX + arrowWidth ||
      minY < minBoundY ||
      maxY > maxBoundY + arrowHeight;

    if (isOutside) {
      // Calculate the offset needed to bring arrow back inside bounds
      let offsetX = 0;
      let offsetY = 0;

      if (minX < minBoundX) {
        offsetX = minBoundX - minX;
      } else if (maxX > maxBoundX + arrowWidth) {
        offsetX = maxBoundX + arrowWidth - maxX;
      }

      if (minY < minBoundY) {
        offsetY = minBoundY - minY;
      } else if (maxY > maxBoundY + arrowHeight) {
        offsetY = maxBoundY + arrowHeight - maxY;
      }

      // Update both the position and all points
      const updatedPoints = element.points.map((coord, i) =>
        i % 2 === 0 ? coord + offsetX : coord + offsetY
      );

      return {
        ...element,
        x: element.x + offsetX,
        y: element.y + offsetY,
        points: updatedPoints,
      };
    }

    return element;
  }

  // Standard handling for other elements
  const elementWidth = element.width || 100;
  const elementHeight = element.height || 100;

  // Calculate boundaries with padding
  const padding = 20;
  const minX = padding;
  const maxX = stageWidth - elementWidth - padding;
  const minY = padding;
  const maxY = stageHeight - elementHeight - padding;

  // Check if element is outside bounds
  const isOutside =
    element.x < minX ||
    element.x > maxX ||
    element.y < minY ||
    element.y > maxY;

  if (isOutside) {
    // Move element back inside bounds
    const constrainedX = Math.max(minX, Math.min(maxX, element.x));
    const constrainedY = Math.max(minY, Math.min(maxY, element.y));

    return {
      ...element,
      x: constrainedX,
      y: constrainedY,
    };
  }

  return element;
};

/**
 * Off-canvas Konva stage is two columns in x: [left strip][main stage overlay].
 * Elements must not sit in the main overlay column (they would render under the main canvas).
 */
export const getOffCanvasMainColumnBounds = (stageWidth) => {
  const w = Number(stageWidth) || 0;
  if (w <= 0) {
    return { mainZoneStart: 0, mainZoneEnd: 0 };
  }
  const nominalTotal =
    FIXED_MAIN_CANVAS_STAGE_WIDTH_PX + FIXED_OFF_CANVAS_SIDE_WIDTH_PX;
  const scale = nominalTotal > 0 ? w / nominalTotal : 1;
  const leftStripWidth = FIXED_OFF_CANVAS_SIDE_WIDTH_PX * scale;
  const mainColumnWidth = FIXED_MAIN_CANVAS_STAGE_WIDTH_PX * scale;
  return {
    mainZoneStart: leftStripWidth,
    mainZoneEnd: leftStripWidth + mainColumnWidth,
  };
};

export const getDeltaXToAvoidOffCanvasMainColumn = (
  absMinX,
  absMaxX,
  stageWidth,
  gap = 0,
) => {
  const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
  const safeGap = Math.max(0, Number(gap) || 0);
  const paddedStart = mainZoneStart - safeGap;
  const paddedEnd = mainZoneEnd + safeGap;
  if (absMaxX <= paddedStart || absMinX >= paddedEnd) {
    return 0;
  }
  const center = (absMinX + absMaxX) / 2;
  const mid = (paddedStart + paddedEnd) / 2;
  return center < mid ? paddedStart - absMaxX : paddedEnd - absMinX;
};

/**
 * Horizontal extent of element in off-canvas stage coordinates (absolute x).
 */
export const getAbsoluteHorizontalExtent = (element, elementType, posX) => {
  switch (elementType) {
    case "Rect":
    case "Text":
      return {
        minX: posX,
        maxX: posX + (element?.width || 0),
      };
    case "Circle": {
      const fallbackRadius =
        element?.radius ||
        Math.max(element?.radiusX || 0, element?.radiusY || 0) ||
        0;
      const rx =
        element?.radiusX !== undefined && element?.radiusX !== null
          ? element.radiusX
          : fallbackRadius;
      return { minX: posX - rx, maxX: posX + rx };
    }
    case "Image": {
      let w = element?.width || 0;
      let h = element?.height || 0;
      
      // For rotated images, calculate the actual horizontal extent
      if (element?.rotation) {
        const rotation = Number(element.rotation) || 0;
        const theta = (rotation * Math.PI) / 180;
        const absCos = Math.abs(Math.cos(theta));
        const absSin = Math.abs(Math.sin(theta));
        
        // Calculate rotated horizontal extent
        const rotatedHorizontalExtent = w * absCos + h * absSin;
        w = rotatedHorizontalExtent;
      }
      
      if (element?.useTopLeftAnchor === true) {
        return { minX: posX, maxX: posX + w };
      }
      return { minX: posX - w / 2, maxX: posX + w / 2 };
    }
    case "Line":
    case "Arrow":
    case "DoubleSidedArrow": {
      const pts = element?.points || [];
      let minP = Infinity;
      let maxP = -Infinity;
      for (let i = 0; i < pts.length; i += 2) {
        minP = Math.min(minP, pts[i]);
        maxP = Math.max(maxP, pts[i]);
      }
      if (!Number.isFinite(minP)) {
        return { minX: posX, maxX: posX };
      }
      return { minX: posX + minP, maxX: posX + maxP };
    }
    default:
      return {
        minX: posX,
        maxX: posX + (element?.width || 0),
      };
  }
};

/**
 * Whether the bbox sits in the left strip [0, mainZoneStart], or (if overlapping
 * the main column) which side its center lies on.
 */
export const getOffCanvasHorizontalStripSide = (minX, maxX, stageWidth) => {
  const w = Number(stageWidth) || 0;
  if (w <= 0 || !Number.isFinite(minX) || !Number.isFinite(maxX)) return null;
  const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(w);
  const tol = 1e-3;
  const hasRightStrip = mainZoneEnd < w - tol;
  const mid = (mainZoneStart + mainZoneEnd) / 2;
  if (maxX <= mainZoneStart + tol) return "left";
  if (hasRightStrip && minX >= mainZoneEnd - tol) return "right";
  if (!hasRightStrip) return "left";
  const cx = (minX + maxX) / 2;
  return cx < mid ? "left" : "right";
};

/**
 * Keep horizontal position locked to the left or right off-canvas strip (no crossing the main column).
 */
export const shiftXToStayInOffCanvasStrip = (
  element,
  elementType,
  x,
  stageWidth,
  lockedSide,
) => {
  if (!lockedSide || !element || !Number.isFinite(stageWidth) || stageWidth <= 0) {
    return x;
  }
  const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);
  const hasRightStrip = mainZoneEnd < stageWidth - 1e-3;
  const effectiveLockedSide =
    lockedSide === "right" && !hasRightStrip ? "left" : lockedSide;
  let cx = x;
  for (let i = 0; i < 16; i++) {
    const { minX, maxX } = getAbsoluteHorizontalExtent(element, elementType, cx);
    let dx = 0;
    if (effectiveLockedSide === "left") {
      if (minX < 0) dx = -minX;
      else if (maxX > mainZoneStart) dx = mainZoneStart - maxX;
    } else if (effectiveLockedSide === "right") {
      if (minX < mainZoneEnd) dx = mainZoneEnd - minX;
      else if (maxX > stageWidth) dx = stageWidth - maxX;
    }
    if (dx === 0) break;
    cx += dx;
  }
  return cx;
};

/**
 * Shift x (group position for lines) so the element's bbox does not overlap the main-column band.
 */
export const applyOffCanvasMainColumnAvoidanceX = (
  element,
  elementType,
  x,
  y,
  stageWidth,
  gap = 0,
) => {
  let cx = x;
  const cy = y;
  for (let i = 0; i < 8; i++) {
    const { minX, maxX } = getAbsoluteHorizontalExtent(element, elementType, cx);
    const d = getDeltaXToAvoidOffCanvasMainColumn(minX, maxX, stageWidth, gap);
    if (d === 0) {
      return { x: cx, y: cy };
    }
    cx += d;
  }
  return { x: cx, y: cy };
};

/**
 * Top-left anchored box: edge clamp + main-column avoidance + edge clamp.
 */
export const clampOffCanvasTopLeftXAwayFromMainColumn = (
  x,
  width,
  stageWidth,
  minXBound = 0,
) => {
  const w = width || 0;
  const lo = Math.max(0, minXBound ?? 0);
  const hi = Math.max(lo, stageWidth - w - lo);
  let cx = Math.max(lo, Math.min(hi, x));
  for (let i = 0; i < 8; i++) {
    const minX = cx;
    const maxX = cx + w;
    const d = getDeltaXToAvoidOffCanvasMainColumn(minX, maxX, stageWidth, lo);
    if (d === 0) {
      break;
    }
    cx += d;
    cx = Math.max(lo, Math.min(hi, cx));
  }
  return cx;
};

/**
 * If x lies in the off-canvas horizontal "main column" band, snap to the nearest edge
 * so shape placement / drag starts do not begin under the main stage overlay.
 */
export const snapOffCanvasPointXOutOfMainColumn = (x, stageWidth) => {
  const w = Number(stageWidth) || 0;
  if (w <= 0) return x;
  const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(w);
  const fx = Number(x);
  if (fx > mainZoneStart && fx < mainZoneEnd) {
    const mid = (mainZoneStart + mainZoneEnd) / 2;
    // Nudge slightly into the side strips so circle/rect draws are not stuck at r=0 on the seam
    const nudge = 2;
    return fx < mid
      ? Math.max(0, mainZoneStart - nudge)
      : Math.min(w, mainZoneEnd + nudge);
  }
  return fx;
};

/**
 * Max radius for a circle centered at centerX (stage coords) so its horizontal extent
 * does not overlap the off-canvas main column, given stroke half-width.
 */
export const maxCircleRadiusAvoidingOffCanvasMainColumn = (
  centerX,
  strokeHalf,
  stageWidth,
) => {
  const w = Number(stageWidth) || 0;
  if (w <= 0) return Infinity;
  const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(w);
  const cx = Number(centerX);
  const sh = Math.max(0, Number(strokeHalf) || 0);
  if (cx < mainZoneStart) {
    return Math.max(0, mainZoneStart - cx - sh);
  }
  if (cx > mainZoneEnd) {
    return Math.max(0, cx - mainZoneEnd - sh);
  }
  return 0;
};

/**
 * Konva Transformer AABB for off-canvas: clamp to stage edges, then lock to left/right strip
 * (from oldBox) so elements cannot be resized from one side of the off-canvas to the other.
 */
export const clampOffCanvasTransformerBox = (
  oldBox,
  newBox,
  stageWidth,
  stageHeight,
) => {
  if (
    !newBox ||
    !Number.isFinite(stageWidth) ||
    !Number.isFinite(stageHeight) ||
    stageWidth <= 0 ||
    stageHeight <= 0
  ) {
    return newBox;
  }
  const clamped = { ...newBox };
  let w = Math.max(0, Number(clamped.width) || 0);
  let h = Math.max(0, Number(clamped.height) || 0);
  let nx = Number(clamped.x) || 0;
  let ny = Number(clamped.y) || 0;

  if (nx < 0) {
    w += nx;
    nx = 0;
  }
  if (ny < 0) {
    h += ny;
    ny = 0;
  }
  if (nx + w > stageWidth) {
    w = Math.max(10, stageWidth - nx);
  }
  if (ny + h > stageHeight) {
    h = Math.max(10, stageHeight - ny);
  }
  if (w < 10 || h < 10) {
    return newBox;
  }

  const ob = oldBox && typeof oldBox === "object" ? oldBox : newBox;
  const lockedSide = getOffCanvasHorizontalStripSide(
    Number(ob.x) || 0,
    (Number(ob.x) || 0) + (Number(ob.width) || 0),
    stageWidth,
  );
  if (!lockedSide) {
    return { ...clamped, x: nx, y: ny, width: w, height: h };
  }

  const { mainZoneStart, mainZoneEnd } = getOffCanvasMainColumnBounds(stageWidth);

  if (lockedSide === "left") {
    if (nx + w > mainZoneStart) {
      w = Math.max(10, mainZoneStart - nx);
    }
    if (nx < 0) {
      w += nx;
      nx = 0;
    }
    if (nx + w > mainZoneStart) {
      w = Math.max(10, mainZoneStart - nx);
    }
  } else {
    if (nx < mainZoneEnd) {
      nx = mainZoneEnd;
    }
    if (nx + w > stageWidth) {
      w = Math.max(10, stageWidth - nx);
    }
    if (nx < mainZoneEnd) {
      nx = mainZoneEnd;
    }
  }

  if (w < 10 || h < 10) {
    return newBox;
  }
  return { ...clamped, x: nx, y: ny, width: w, height: h };
};

/**
 * Function to ensure all elements are within bounds
 */
export const ensureAllElementsInBounds = ({
  stageRef,
  texts,
  rectangles,
  circles,
  arrows,
  images,
  lines,
  setTexts,
  setRectangles,
  setCircles,
  setArrows,
  setImages,
  setLines,
}) => {
  // Check and fix main canvas elements
  if (stageRef?.current) {
    const mainElements = [
      ...texts.map((t) => ({
        ...t,
        type: t.type || "Text",
      })),
      ...rectangles.map((r) => ({
        ...r,
        type: r.type || "Rect",
      })),
      ...circles.map((c) => ({
        ...c,
        type: c.type || "Circle",
      })),
      ...arrows.map((a) => ({
        ...a,
        type: a.type || "Arrow",
      })),
      ...images.map((i) => ({
        ...i,
        type: i.type || "Image",
      })),
      ...lines.map((l) => ({
        ...l,
        type: l.type || "Line",
      })),
    ];

    mainElements.forEach((element) => {
      const constrainedElement = ensureElementInBounds(element, stageRef);
      if (
        constrainedElement.x !== element.x ||
        constrainedElement.y !== element.y
      ) {
        // Update the element state based on type
        switch (element.type) {
          case "Text":
            setTexts((prev) =>
              prev.map((t) => (t.id === element.id ? constrainedElement : t))
            );
            break;
          case "Rect":
            setRectangles((prev) =>
              prev.map((r) => (r.id === element.id ? constrainedElement : r))
            );
            break;
          case "Circle":
            setCircles((prev) =>
              prev.map((c) => (c.id === element.id ? constrainedElement : c))
            );
            break;
          case "Arrow":
            setArrows((prev) =>
              prev.map((a) => (a.id === element.id ? constrainedElement : a))
            );
            break;
          case "Image":
            setImages((prev) =>
              prev.map((i) => (i.id === element.id ? constrainedElement : i))
            );
            break;
          case "Line":
            setLines((prev) =>
              prev.map((l) => (l.id === element.id ? constrainedElement : l))
            );
            break;
        }
      }
    });
  }
};
